/**
 * exportCsv — Round 27. Frontend-only CSV download of rows already loaded
 * in the browser (respects whatever filters produced them). No API calls.
 *
 *   exportRowsToCsv("epos.csv",
 *     [{ key: "epoNumber", label: "EPO #" },
 *      { key: "createdBy", label: "Created By" },
 *      { label: "Status", get: (r) => r.status?.toUpperCase() }],
 *     rows);
 *
 * Columns: `key` reads the field directly; `get` computes a value.
 * Values are CSV-escaped (quotes doubled, wrapped when needed) and a UTF-8
 * BOM is prepended so Excel opens accented text correctly.
 */
export function exportRowsToCsv(filename, columns, rows) {
    const esc = (v) => {
        if (v === null || v === undefined) return "";
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = columns.map((c) => esc(c.label)).join(",");
    const lines = (rows || []).map((r) =>
        columns.map((c) => esc(c.get ? c.get(r) : r?.[c.key])).join(",")
    );
    const csv = "\uFEFF" + [header, ...lines].join("\r\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * exportAllPagesToCsv — Round 29. For server-paginated tables: walks the
 * SAME endpoint the table already uses (same filters), page by page, then
 * downloads everything as one CSV. `fetchPage(page, pageSize)` must return
 * `{ rows, total }`. Progress is reported via `onProgress(loaded, total)`.
 * Hard stop at 200 pages (100k rows at the default 500/page) as a sanity rail.
 */
/**
 * fetchAllPages — Round 31. The page-walking core, reusable on its own:
 * tables use it to load the COMPLETE dataset for full-data sorting and
 * instant client-side filtering.
 */
export async function fetchAllPages({ fetchPage, total, pageSize = 500, onProgress }) {
    const all = [];
    let page = 1;
    for (;;) {
        const res = await fetchPage(page, pageSize);
        const rows = (res && res.rows) || [];
        const t = total ?? (res && res.total) ?? 0;
        all.push(...rows);
        if (onProgress) onProgress(Math.min(all.length, t || all.length), t || all.length);
        if (rows.length === 0 || (t && all.length >= t) || page >= 200) break;
        page += 1;
    }
    return all;
}

export async function exportAllPagesToCsv({ filename, columns, fetchPage, total, pageSize = 500, onProgress }) {
    const all = await fetchAllPages({ fetchPage, total, pageSize, onProgress });
    exportRowsToCsv(filename, columns, all);
    return all.length;
}

export default exportRowsToCsv;
